import React, { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "sonner";
import AppRoutes from "./router/AppRoutes";
import ScrollToTop from "./components/ScrollToTop";
import useAxiosInterceptor from "./hooks/useAxiosInterceptor";
import { isAuthenticated, clearAuth } from "./utils/auth";
import { captureUTMParams } from "./utils/utmTracker";
import { trackSiteVisit } from "./utils/siteTraffic";
import { ClarityPlugin } from "./utils/clarity";

function App() {
  const location = useLocation();
  const navigate = useNavigate();

  // Setup axios interceptors with navigation
  useAxiosInterceptor();

  // Check token expiration on app initialization and route changes
  useEffect(() => {
    checkTokenValidity();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  useEffect(() => {
    if (location?.pathname) {
      ClarityPlugin.page(location.pathname);
    }
    // Facebook Pixel requires an explicit PageView on every SPA navigation;
    // the snippet in index.html only fires once on the initial HTML load.
    if (typeof window !== "undefined" && typeof window.fbq === "function") {
      window.fbq("track", "PageView");
    }
  }, [location]);

  useEffect(() => {
    ClarityPlugin.init();
  }, []);

  useEffect(() => {
    captureUTMParams();
  }, []);

  useEffect(() => {
    trackSiteVisit();
  }, []);
  useEffect(() => {
    // Define public routes that should NOT be saved as lastRoute
    const publicRoutes = [
      "/",
      "/login",
      "/registration",
      "/testimonial",
      "/salaries",
      "/comparisons",
      "/city-comparison",
      "/share",
      "/jobs", // Public job detail pages like /jobs/:slug
      "/unsubscribe",
      "/poll/vote", // Public poll-vote result landing (reached from campaign emails)
      "/poll/take", // Public multi-select poll vote form (reached from campaign emails)
    ];

    // Check if current path is a public route
    const isPublicRoute = publicRoutes.some((route) => {
      if (route === "/jobs") {
        // Only match /jobs/:slug pattern (public), not /jobs or /job-detail (protected)
        return (
          location.pathname.startsWith("/jobs/") &&
          location.pathname.split("/").length === 3
        );
      }
      if (route === "/share") {
        return location.pathname.startsWith("/share/");
      }
      return location.pathname === route;
    });

    // Only save lastRoute if user is authenticated AND on a protected route
    if (isAuthenticated() && !isPublicRoute) {
      let routeToSave = location.pathname + location.search;

      // If the route is /messaging/{userId}, save it as /messaging instead
      if (location.pathname.match(/^\/messaging\/\d+$/)) {
        routeToSave = "/messaging";
      }

      localStorage.setItem("lastRoute", routeToSave);
    }
  }, [location]);

  const checkTokenValidity = () => {
    // Skip check - ProtectedRoute component handles auth check
    // Token expiration is validated by server via API interceptor
    return;
  };

  return (
    <>
      <ScrollToTop />
      <AppRoutes />
      {/* sonner Toaster — premium chrome by default (richColors gives
          branded success/error tinting; closeButton shows X on hover;
          visibleToasts caps the stack). One mount at the app shell makes
          every toast.* call across the app render with the same look. */}
      <Toaster
        position="top-right"
        richColors
        // closeButton
        expand
        duration={3000}
        visibleToasts={3}
      />
    </>
  );
}

export default App;
